import React, { useMemo } from 'react';
import { Modal, Tree, Empty, Typography } from 'antd';

const { Text } = Typography;

interface Props {
  copyKeywordForm: any;
  categoriesWithBuckets: any[];
  modalOpen: boolean;
  handleOk: (action: string) => void;
  handleCancel: (action: string) => void;
  loading?: boolean;
}

const CopyKeyword = ({ copyKeywordForm, categoriesWithBuckets, modalOpen, handleOk, handleCancel, loading }: Props) => {
  const platform = copyKeywordForm.values.platform?.toLowerCase();

  const selectedBucketIds: string[] = copyKeywordForm.values.bucket_ids || [];
  const overwriteTargets: Array<{ bucket_id: string; keyword_id: string }> =
    copyKeywordForm.values.overwrite_targets || [];

  // "Add to bucket" and "overwrite a keyword" are mutually exclusive per bucket, so lock out the
  // other mode once one is chosen for a given bucket.
  const bucketsInAddMode = new Set(selectedBucketIds);
  const bucketsInOverwriteMode = new Set(overwriteTargets.map((target) => target.bucket_id));

  // Category (expandable) -> Bucket (tick to ADD) -> Keyword (tick to OVERWRITE) for the selected platform.
  const treeData = useMemo(() => {
    return (categoriesWithBuckets || [])
      .filter((category: any) => category.platform === platform)
      .map((category: any) => ({
        title: category.category_name,
        key: `cat-${category._id}`,
        checkable: false,
        selectable: false,
        children: (category.buckets || []).map((bucket: any) => ({
          title: bucket.bucket_name,
          key: `bucket-${bucket._id}`,
          selectable: false,
          disabled: bucketsInOverwriteMode.has(bucket._id),
          children: (bucket.keywords || []).map((keyword: any) => ({
            title: (
              <Text>
                {keyword.keywords_label || keyword.keywords || '(untitled keyword)'}{' '}
                <Text type="secondary">(overwrite)</Text>
              </Text>
            ),
            key: `kw-${bucket._id}-${keyword._id}`,
            selectable: false,
            disabled: bucketsInAddMode.has(bucket._id),
          })),
        })),
      }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoriesWithBuckets, platform, selectedBucketIds, overwriteTargets]);

  const checkedKeys = [
    ...selectedBucketIds.map((id: string) => `bucket-${id}`),
    ...overwriteTargets.map((target: any) => `kw-${target.bucket_id}-${target.keyword_id}`),
  ];

  const onCheck = (checked: any) => {
    // checkStrictly returns { checked, halfChecked }; keep bucket and keyword ticks independent.
    const keys: string[] = (Array.isArray(checked) ? checked : checked.checked).map(String);

    const bucketIds = keys.filter((key) => key.startsWith('bucket-')).map((key) => key.replace('bucket-', ''));

    const overwriteTargets = keys
      .filter((key) => key.startsWith('kw-'))
      .map((key) => {
        const [, bucketId, keywordId] = key.split('-');
        return { bucket_id: bucketId, keyword_id: keywordId };
      });

    copyKeywordForm.setFieldValue('bucket_ids', bucketIds);
    copyKeywordForm.setFieldValue('overwrite_targets', overwriteTargets);
  };

  return (
    <Modal
      title={`Copy Keyword to ${copyKeywordForm.values.platform}`}
      open={modalOpen}
      onOk={() => handleOk('copy_keyword')}
      onCancel={() => handleCancel('copy_keyword')}
      okText="Copy"
      okButtonProps={{ disabled: !checkedKeys.length }}
      confirmLoading={loading}
    >
      {treeData.length ? (
        <Tree checkable checkStrictly checkedKeys={checkedKeys} onCheck={onCheck} treeData={treeData} />
      ) : (
        <Empty description="No categories found for this platform" />
      )}
    </Modal>
  );
};

export default CopyKeyword;
